import type { DaperPerson } from "@bloomberg/intercom";

import type { LiveBlogContributor, Person } from "~types/types";
import {
    type Person as WardenclyffePerson,
    PersonRole,
} from "~types/wardenclyffe";

import { mockHydratedImage } from "./mockMediaBuilder";
import { type DeepPartial, deepWrap, mockHandler } from "./mockUtils";

type MockDaperPersonParams = {
    [K in keyof DaperPerson]?: DaperPerson[K];
} & { index?: number };

const BASE_PERSON_MOCK: Person = {
    editorialBoard: false,
    id: "",
    name: "",
    photoDisplayed: false,
    roles: [],
    slug: "",
    title: "",
};

const BASE_LIVEBLOG_CONTRIBUTOR_MOCK: LiveBlogContributor = {
    id: "",
    name: "",
    slug: "",
    thumbnail: {},
    title: "",
};

const EMPTY_DAPER_PERSON: DaperPerson = {
    address1: "",
    address2: "",
    address3: "",
    birthDay: "",
    birthPlace: "",
    city: "",
    company: "",
    country: "",
    displayNote: "",
    email: "",
    fax: "",
    firstName: "",
    fullName: "",
    hobbies: [],
    id: "",
    image: {
        id: "",
        _links: {
            self: {
                href: "",
            },
        },
    },
    lastName: "",
    lastNameFirst: false,
    middleName: "",
    nickName: "",
    phone: "",
    photoDisplayed: false,
    politicalAffiliation: "",
    slug: "",
    state: "",
    title: "",
    uuid: 0,
    webVisibility: false,
    zip: "",
    _links: {
        authoredStories: {
            href: "",
        },
        careerHistory: {
            href: "",
        },
        company: {
            href: "",
            webTicker: "",
        },
        details: {
            href: "",
        },
        self: {
            href: "",
        },
    },
};

export const mockEditor = ({ name }: { name: string }): Person => {
    return {
        ...BASE_PERSON_MOCK,
        bio: "An editor's bio",
        editorialBoard: true,
        photoDisplayed: true,
        roles: [PersonRole.Editor],
        slug: "editor-id/editor-name",
        thumbnail: mockHydratedImage({ alt: "Editor Name Alt" }),
        twitter: "https://www.twitter.com/editor-name",
        name,
    };
};

export const mockColumnist = ({ name }: { name: string }): Person => {
    return {
        ...BASE_PERSON_MOCK,
        bio: "An columnist's bio",
        editorialBoard: false,
        roles: [PersonRole.Columnist],
        slug: "columnist-id/columnist-name",
        twitter: "https://www.twitter.com/columnist-name",
        name,
    };
};

export const mockAuthor = (
    overrides: DeepPartial<WardenclyffePerson> = {}
): WardenclyffePerson => {
    return new Proxy(
        deepWrap(overrides) as object,
        mockHandler
    ) as WardenclyffePerson;
};

export const mockLiveBlogContributor = ({
    index,
}: { index?: number } = {}): LiveBlogContributor => {
    return {
        ...BASE_LIVEBLOG_CONTRIBUTOR_MOCK,
        id: `test-liveblog-contributor-id-${index || 1}`,
        name: `Test Liveblog Contributor Name ${index || 1}`,
        slug: `/test-liveblog-contributor-slug-${index || 1}`,
        thumbnail: mockHydratedImage({
            alt: `Test Liveblog Contributor Thumbnail Alt ${index || 1}`,
            baseUrl: `/test-liveblog-contributor-thumbnail-url-${index || 1}`,
        }),
        title: `Test Liveblog Contributor Title ${index || 1}`,
        twitter: `https://www.twitter.com/test-liveblog-contributor-twitter-${
            index || 1
        }`,
    };
};

export const mockDaperPerson = (params: MockDaperPersonParams): DaperPerson => {
    const i = params.index || "";

    return {
        ...EMPTY_DAPER_PERSON,
        id: `author-id-${i}`,
        fullName: `Author Full Name ${i}`,
        slug: `www.bloomberg.com/author-slug-${i}`,
        ...params,
    };
};
